import {
  base_colorAction,
  base_colorActionHover,
  cta_alertButtonBackground,
  cta_alertButtonBackgroundHover,
  cta_alertButtonBorder,
  cta_buttonPrimaryBackground,
  cta_buttonPrimaryBackgroundHover,
  cta_buttonPrimaryBorder,
  cta_buttonPrimaryText,
  cta_buttonSecondaryBackground,
  cta_buttonSecondaryBackgroundHover,
  cta_buttonSecondaryBorder,
  cta_linkTextPrimary,
  cta_noshellButtonAnimatedCircle,
  cta_noshellButtonBackgroundHover,
  cta_spotlightBackgroundHover,
  cta_spotlightButtonBackground,
  cta_spotlightButtonBorder,
  grey100,
  grey300,
  grey400,
} from 'ext-ui/style/colors'
import {
  button_hGapSpacing,
  button_hSpacing,
  button_vSpacing,
  spacing,
} from 'ext-ui/style/spacing'
import { button_transitionSpeed } from 'ext-ui/style/transitionSpeeds'
import { font_family_weight_medium, font_md } from 'ext-ui/style/typography'
import { css } from 'styled-components'

export default css`
  position: relative;
  display: inline-flex;
  padding: ${button_vSpacing} ${button_hSpacing};
  justify-content: center;
  align-items: center;
  gap: ${button_hGapSpacing};
  flex-shrink: 0;
  border-radius: ${spacing(2)};
  overflow: hidden;
  cursor: pointer;
  transition:
    background-color ${button_transitionSpeed} ease-out,
    opacity ${button_transitionSpeed} ease-out,
    color ${button_transitionSpeed} ease-out,
    fill ${button_transitionSpeed} ease-out;
  font-family: ${font_family_weight_medium};
  ${font_md};

  &.dropShadow {
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.15);
  }

  // VARIANTS
  &.primary {
    outline: 2px solid ${cta_buttonPrimaryBorder};
    outline-offset: -2px;
    background-color: ${cta_buttonPrimaryBackground};
    color: ${cta_buttonPrimaryText};
    & g {
      fill: ${cta_buttonPrimaryText};
    }

    &:hover {
      background-color: ${cta_buttonPrimaryBackgroundHover};
    }
  }

  &.secondary {
    outline: 1px solid ${cta_buttonSecondaryBorder};
    outline-offset: -1px;
    background-color: ${cta_buttonSecondaryBackground};
    color: ${base_colorAction};
    & g {
      fill: ${base_colorAction};
    }

    &:hover {
      background-color: ${cta_buttonSecondaryBackgroundHover};
      color: ${base_colorActionHover};
      & g {
        fill: ${base_colorActionHover};
      }
    }

    &.dropShadow {
      box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
    }
  }

  &.severe {
    border: 2px solid ${cta_alertButtonBorder};
    background-color: ${cta_alertButtonBackground};
    color: ${cta_buttonPrimaryText};
    & g {
      fill: ${cta_buttonPrimaryText};
    }

    &:hover {
      background-color: ${cta_alertButtonBackgroundHover};
    }
  }

  &.toolbar {
    border: 1px solid ${grey400};
    padding: ${spacing(2)} ${spacing(4)};
    background-color: ${grey100};
    color: ${cta_linkTextPrimary};
    font-family: ${font_family_weight_medium};
    ${font_md};
    height: 38px;
    min-height: 38px;

    &:hover {
      background-color: ${grey300};
      color: ${base_colorActionHover};
      & g {
        fill: ${base_colorActionHover};
      }
    }
  }

  &.spotlight {
    border: 2px solid ${cta_spotlightButtonBorder};
    background-color: ${cta_spotlightButtonBackground};
    color: ${cta_buttonPrimaryText};
    & g {
      fill: ${cta_buttonPrimaryText};
    }

    &:hover {
      background-color: ${cta_spotlightBackgroundHover};
    }
  }

  &.no-shell {
    background-color: unset;
    color: ${base_colorAction};
    & g {
      fill: ${base_colorAction};
    }

    &:hover {
      background-color: ${cta_noshellButtonBackgroundHover};
      color: ${base_colorActionHover};
      & g {
        fill: ${base_colorActionHover};
      }
    }
  }

  &.no-shell-no-padding {
    background-color: unset;
    border-radius: 0;
    height: unset;
    padding: 0;
    color: ${base_colorAction};
    & g {
      fill: ${base_colorAction};
    }

    &:hover {
      color: ${base_colorActionHover};
      & g {
        fill: ${base_colorActionHover};
      }
    }
  }

  &.no-border {
    border: none;
    box-shadow: none;
    background-color: unset;
    height: unset;
    color: ${base_colorAction};
    & g {
      fill: ${base_colorAction};
    }
    &:hover {
      background-color: ${cta_noshellButtonBackgroundHover};
      color: ${base_colorActionHover};
      & g {
        fill: ${base_colorActionHover};
      }
    }
  }

  &.text {
    border: none;
    box-shadow: none;
    background-color: unset;
    height: unset;
    padding: 0;
    color: ${base_colorAction};
    & g {
      fill: ${base_colorAction};
    }

    &:hover {
      color: ${base_colorActionHover};
      & g {
        fill: ${base_colorActionHover};
      }
    }
  }

  // STATES
  &.disabled,
  &.loading {
    opacity: 50%;
    cursor: not-allowed;
  }

  // CHILDREN
  & > .ripple-effect {
    position: absolute;
    border-radius: 50%;
    opacity: 0;

    &.isAnimating {
      animation-name: scaleAndFadeAnimation;
      animation-duration: 500ms;
      animation-iteration-count: 1;
      animation-direction: linear;
    }

    // VARIANTS
    &.primary {
      background-color: ${cta_buttonPrimaryBackground};
    }

    &.secondary {
      background-color: ${cta_buttonSecondaryBackground};
    }

    &.severe {
      background-color: ${cta_alertButtonBackground};
    }

    &.spotlight {
      background-color: ${cta_spotlightButtonBackground};
    }

    &.no-shell {
      background-color: ${cta_noshellButtonAnimatedCircle};
    }

    &.no-shell-no-padding {
      background-color: unset;
    }

    &.toolbar {
      background-color: ${grey300};
    }
  }

  @keyframes scaleAndFadeAnimation {
    0% {
      transform: scale(0);
      opacity: 1;
    }
    100% {
      transform: scale(12);
      opacity: 0;
    }
  }
`
